import * as React from 'react'
import Async from 'react-select/async'
import AsyncCreatable, {
  AsyncCreatableProps as Props,
} from 'react-select/async-creatable'
import useResizeObserver from 'use-resize-observer'
import { throttle, isArray, isEmpty } from 'lodash'
import {
  mergeStyles,
  MultiValueProps,
  ControlProps,
  GroupBase,
} from 'react-select'
import { getSelectCustomStyles } from './multi-select.styles'
import { identifiers } from './multi-select.constants'
import { SmartControl } from './smart-control'
import { MultiValue as DefaultMultiValue } from './multi-value'
import { OptionTypeBase } from '../../interface/optionTypeBase'

export interface MultiSelectProps<OptionType extends OptionTypeBase>
  extends Props<OptionType, boolean, GroupBase<OptionType>> {
  readOnly?: boolean
  creatable?: boolean
  optionGapInPx?: number
  displayRows?: number
  maxTagLength?: number
  optionValidatorFn?(item: OptionType): boolean
  optionIsWarningFn?(option: OptionType): boolean
  onTagClick?(option: OptionType): void
  onContextMenuClick?(event: React.MouseEvent, option: OptionType): void
  onKeyDown?(event: React.KeyboardEvent<HTMLElement>): void
}

function MultiSelectComponent<
  OptionType extends OptionTypeBase = { label: string; value: string }
>(
  {
    id,
    components: selectComponents,
    styles,
    readOnly,
    creatable,
    optionGapInPx = 2,
    displayRows = 2,
    maxTagLength,
    cacheOptions = true,
    defaultOptions = true,
    optionValidatorFn,
    optionIsWarningFn,
    onTagClick,
    onContextMenuClick,
    onKeyDown,
    ...props
  }: MultiSelectProps<OptionType>,
  innerRef: React.RefObject<any>
) {
  const selectRef = React.useRef(null)
  const [optionsContainerWidth, setOptionsContainerWidth] = React.useState(0)

  const updateOptionsContainerWidth = React.useCallback(
    throttle(() => {
      const container = document.querySelector(
        `[data-id=${id}__${identifiers.control}] > div`
      )

      if (container) {
        setOptionsContainerWidth(container.clientWidth)
      }
    }, 250),
    [id, setOptionsContainerWidth]
  )

  const { ref } = useResizeObserver({
    ref: selectRef,
    onResize: updateOptionsContainerWidth,
  })

  const onChange = React.useMemo(
    () => (readOnly ? () => {} : props.onChange),
    [readOnly, props.onChange]
  )

  const Control = React.useMemo(
    () => (controlProps: ControlProps<OptionType>) =>
      (
        <SmartControl<OptionType>
          {...controlProps}
          id={id}
          optionGapInPx={optionGapInPx}
          optionsContainerWidthInPx={optionsContainerWidth}
          displayRows={displayRows}
        />
      ),
    [optionGapInPx, optionsContainerWidth, displayRows, id]
  )

  const componentStyles = React.useMemo(
    () =>
      mergeStyles(
        getSelectCustomStyles({
          id,
          optionGapInPx,
          readOnly,
          displayRows,
          optionsContainerWidth,
        }),
        styles
      ),
    [optionGapInPx, readOnly, styles, optionsContainerWidth, id, displayRows]
  )

  const customComponents = React.useMemo(() => {
    const { MultiValue: CustomMultiValue, ...otherComponents } =
      selectComponents || {}
    const MultiValueComponent = CustomMultiValue || DefaultMultiValue

    function MultiValue(multiValueProps: MultiValueProps<OptionType>) {
      return (
        <MultiValueComponent
          {...multiValueProps}
          id={id}
          readOnly={readOnly}
          maxLength={maxTagLength}
          optionValidatorFn={optionValidatorFn}
          optionIsWarningFn={optionIsWarningFn}
          onTagClick={onTagClick}
          onContextMenuClick={onContextMenuClick}
        />
      )
    }

    return {
      Control,
      MultiValue,
      ...otherComponents,
    }
  }, [Control, selectComponents, id, readOnly, maxTagLength])

  const Component = creatable ? AsyncCreatable : Async

  const onKeyDownHandler = (event: React.KeyboardEvent<HTMLElement>) => {
    const keysToIgnore = ['Backspace', 'Delete']
    const currentValue = (event.target as HTMLInputElement)?.value
    const optionsValue = props.value

    if (keysToIgnore.includes(event.key) && isEmpty(currentValue)) {
      if (isArray(optionsValue) && optionsValue.length) {
        const lastOption = optionsValue[optionsValue.length - 1]

        if (lastOption && lastOption?.isNonDeletable) {
          event.preventDefault()
        }
      }
    }
    if (onKeyDown) {
      onKeyDown(event)
    }
  }

  return (
    <div ref={ref}>
      <Component
        {...props}
        ref={innerRef}
        isMulti
        cacheOptions={cacheOptions}
        defaultOptions={defaultOptions}
        menuIsOpen={readOnly ? false : props.menuIsOpen}
        onChange={onChange}
        inputValue={readOnly ? '' : props.inputValue}
        components={customComponents}
        styles={componentStyles}
        onKeyDown={(event) => onKeyDownHandler(event)}
      />
    </div>
  )
}

export const MultiSelect = React.forwardRef(MultiSelectComponent) as <
  OptionType extends OptionTypeBase = { label: string; value: string }
>(
  props: MultiSelectProps<OptionType> & { ref?: React.RefObject<any> }
) => ReturnType<typeof MultiSelectComponent>
